import {useEffect, useState} from 'react';
import {useConstCallback} from '@netx/core-components/hooks/useEvent';

export interface UseTabsProps {
	afterChange?: (newIndex: number, oldIndex: number) => void;
	/**
	 * Provides the ability to check the new tab index vs the current tab index
	 * and return `false` if the switch should be ignored
	 */
	beforeChange?: (newIndex: number, currentIndex: number) => boolean | void;
	/**
	 * Index to be considered `active`\
	 * NOTE: Changes from this will supress before/after callbacks
	 */
	index?: number;
}

/**
 * Hook for managing the active tab index
 * @param options 
 * @example
    const handleBeforeChange = useCallback<UseTabsProps['beforeChange']>((newIndex, currentIndex) => {
        return foo !== "bar";
    }, [foo]);
    const tabs = useTabs({
        beforeChange: handleBeforeChange,
    });
    return (
        <Tabs
            onChange={tabs.handleChange}
            value={tabs.activeTabIndex}
        >
            <Tab />
            <Tab />
        </Tabs>
    );
 */
export const useTabs = ({
	afterChange,
	beforeChange,
	index,
}: UseTabsProps = {}) => {
	const [activeTabIndex, setActiveTabIndex] = useState(index || 0);

	// TODO: we should rename this to `initialIndex` and ignore any changes after mount
	// if one wants to change the index manually, they should use the `setTab` method
	useEffect(() => {
		setActiveTabIndex(index || 0);
	}, [index]);

	const setTab = useConstCallback((index: number) => {
		setActiveTabIndex((activeTabIndex) => {
			// If the index is the same, or the `beforeChange` callback opts to prevent
			if (
				activeTabIndex === index ||
				beforeChange?.(index, activeTabIndex) === false
			)
				return activeTabIndex;

			// Notify and update
			afterChange?.(index, activeTabIndex);
			return index;
		});
	});

	const handleChange = useConstCallback((event: any, index: number) =>
		setTab(index),
	);

	return {
		activeTabIndex,
		handleChange,
		setTab,
	};
};

export default useTabs;
